import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Subscription, BehaviorSubject, Observable, combineLatest, of, concat } from "rxjs";
import { map, tap, catchError, switchMap } from "rxjs/operators";
import { GridOptions, ColDef, GridApi, RowSelectionOptions, SelectionColumnDef } from "ag-grid-community"; 
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { WellService } from "../../../common/services/well.service";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { Option } from "../../../common/models/flow.dto";
import { Permit, RegeneratePermitRequest, RegeneratedPermitResult, DocumentUploadStatus } from "../../../common/models/permit.model";
import { PermitService } from "../../../common/services/permit.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { ApplicationsService } from "../../../applications/services/applications.service";
import { MetadataSearchSpec, ApplicationType, ApplicationState } from "../../../applications/models/applications.models";
import { PendingFlow } from '../../../flow/models/flow.model';

import { Domain } from "../../../builder/models/domain.model";
import { LoaderService } from "../../../common/services/loader.service";
import { IconActionRendererComponent } from "../../../common/components/ag-grid/icon-action-renderer.component";
import { SchematicsPreviewComponent } from "./schematics-preview/schematics-preview.component";

@Component({
    selector: 'app-regenerate-permit',
    templateUrl: './regenerate-permit.component.html',
    styleUrls: ['./regenerate-permit.component.scss'],
    standalone: false
})
export class RegeneratePermitComponent implements OnInit, OnDestroy {

    apiNumbers$: Observable<Option[]>;

    apiColumn: ColDef = {
        field: "text",
        tooltipField: "text",
        headerName: "API #",
        headerTooltip: "API #",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 200
    } as ColDef;

	applicationData: PendingFlow[] = [];

    currentStep: PermitRegenerationSteps = PermitRegenerationSteps.SelectWell;

    downloadColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Download / Preview",
        headerTooltip: "Download / Preview",
        field: "",
        filter: false,
        minWidth: 200,
        cellStyle: { 'text-align': 'center', 'line-height': 1.75 },
        cellRenderer: IconActionRendererComponent,
        cellRendererParams: {
            buttons: [
                {
                    action: (obj: any) => this.download(obj),
                    label: "Download",
                    icon: "download"
                }
            ]
        }
    }

    @ViewChild('downloadLink', { static: true }) public downloadLink: ElementRef;

    generator$: Observable<RegeneratedPermitResult[]>;

    permitIdColumn: ColDef = {
        headerName: "Permit #",
        headerTooltip: "Permit #",
        field: "id",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        tooltipField: "id",
        minWidth: 125
    } as ColDef;

    permitTypeColumn: ColDef = {
        headerName: "Permit Type",
        headerTooltip: "Permit Type",
        field: "type",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        valueGetter: this.formatType("type"),
        tooltipValueGetter: this.formatType("type"),
        minWidth: 200
    } as ColDef;

    permit$: Observable<Permit[]>;
    permitSteps = PermitRegenerationSteps;

    selectionColumnDef: SelectionColumnDef = {
        suppressAutoSize: true,
        suppressSizeToFit: true,
        width: 75
    } as SelectionColumnDef;

    @ViewChild(SchematicsPreviewComponent) previewComponent: SchematicsPreviewComponent;

    showResults: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    types: Domain[];

    wellAPIColumn: ColDef = {
        headerName: "API #",
        headerTooltip: "API #",
        field: "apiNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
    } as ColDef;

    form: FormGroup = new FormGroup({});

	gridOptions: GridOptions;
	gridApi?: GridApi;

	permitGridOptions: GridOptions;
	permitGridApi?: GridApi;
    permitSearchStarted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    permitToPreviewSchematic: Permit = null;

    regeneratedGridOptions: GridOptions;
	regeneratedGridApi?: GridApi; 

    regenPermitIdColumn: ColDef = {
        headerName: "Permit #",
        headerTooltip: "Permit #",
        field: "id",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        tooltipField: "id",
        minWidth: 125
    } as ColDef;

    get selectedPermits(): Permit[] {
        if (this.form == null || this.form.get("permits") == null) return [];
        return <Permit[]>this.form.get("permits")?.value ?? [];
    }

    statuses = DocumentUploadStatus;

    steps: Option[] = [
        { id: PermitRegenerationSteps.SelectWell.toString(), idNumeric: PermitRegenerationSteps.SelectWell, text: "Select Well" } as Option,
        { id: PermitRegenerationSteps.SelectPermits.toString(), idNumeric: PermitRegenerationSteps.SelectPermits, text: "Select Permit(s)" } as Option,
        { id: PermitRegenerationSteps.ConfirmDocuments.toString(), idNumeric: PermitRegenerationSteps.ConfirmDocuments, text: "Confirm Document(s)" } as Option,
        { id: PermitRegenerationSteps.SaveDocuments.toString(), idNumeric: PermitRegenerationSteps.SaveDocuments, text: "Save Document(s)" } as Option
    ];

    step1Header: SubHeader = new SubHeader({ title: "Regenerate a Permit ", buttons: [{ name: "Search for Permits", method: () => this.searchPermits() }] });
    step2Header: SubHeader = new SubHeader({ title: "Regenerate a Permit - Select Permits", buttons: [{ name: "Regenerate Permit(s)", method: () => this.regeneratePermits() }, { name: "Back to Previous", method: () => this.setStep(PermitRegenerationSteps.SelectWell) }] });

    step3Header: SubHeader = new SubHeader({ title: "Regenerate a Permit - Confirm Documents", buttons: [{ name: "Upload Document(s)", method: () => this.uploadDocuments() }, { name: "Back to Previous", method: () => this.setStep(PermitRegenerationSteps.SelectPermits) }] });
    step4Header: SubHeader = new SubHeader({ title: "Regenerate a Permit - Save Documents", buttons: [{ name: "Restart", method: () => this.restart() }] });

    subscription: Subscription = new Subscription();

    uploadingDocuments: RegeneratedPermitResult[] = [];

    wheresMyAPI: string = "Only those well API numbers for which RICS has a permit on file are shown in the list";
    wheresMyPermit: string = "Only those permits which originated in RICS are available for regeneration";

    constructor(private builder: FormBuilder, private subheaderService: SubHeaderService, private domainService: DomainDefinitionService, private loader: LoaderService,
		private wellService: WellService, private dialogService: DynamicDialogService, private formatter: GridFormatterService, private permitService: PermitService,
		private applicationService: ApplicationsService) { }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.step1Header);
        this.form = this.builder.group({
            api: ["", [Validators.required]],
            permits: [[], [Validators.minLength(1), Validators.required]],
            documents: [[], [Validators.minLength(1), Validators.required]]
        })
		this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns([this.apiColumn], false);
		this.gridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            // this.gridApi.autoSizeColumns(['text'], false);
            this.gridApi.sizeColumnsToFit();
        };
		this.gridOptions.rowSelection = {
            mode: "singleRow",
            checkboxes: true,
            checkboxLocation: 'selectionColumn',
        } as RowSelectionOptions;
        this.permitGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns([this.permitIdColumn, this.wellAPIColumn, this.permitTypeColumn], false, true, true);
		this.permitGridOptions.onGridReady = (params) => { 
            this.permitGridApi = params.api; 
            this.permitGridApi.sizeColumnsToFit();
        };
        this.permitGridOptions.onFirstDataRendered = () => {
            let selectedPermits = <Permit[]>this.form.get("permits").value;
            if (selectedPermits != null && selectedPermits.length > 0) {
                if (this.permitGridApi) {
					this.permitGridApi.forEachNode(n => { 
						if (n.data != null) { 
                            let rowPermit = <Permit>n.data;
                            if (selectedPermits.findIndex(p => p.id == rowPermit.id) !== -1) {
                                n.setSelected(true);
                            }
                        }
                    });
                    
                }
            }           
        };
        this.permitGridOptions.rowSelection = {
            mode: "multiRow",
            checkboxes: true,
            headerCheckbox: true,
            selectAll: "filtered"
        } as RowSelectionOptions;
		this.regeneratedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns([this.regenPermitIdColumn, this.downloadColumn], false, true, true);
		this.regeneratedGridOptions.onGridReady = (params) => { 
            this.regeneratedGridApi = params.api; 
            this.regeneratedGridApi.sizeColumnsToFit();
        };
        this.regeneratedGridOptions.rowSelection = {
            mode: "multiRow",
            checkboxes: true,
            headerCheckbox: true,
            selectAll: "filtered",
            enableClickSelection: false
        } as RowSelectionOptions;
        this.apiNumbers$ = this.wellService.getAPINumbers(true).pipe(
            catchError((error) => {
                console.log(error);
                this.dialogService.showError();
                return of([]);
            }),
            map((apis: string[]) => {
                let list: Option[] = [];
                apis.forEach(a => {
                    list.push({ id: a, text: a } as Option);
                });
                return list;
            })
        );
        let type$ = this.domainService.getValues("TYP_FORM").pipe(
            tap((values: any) => { this.types = values; })
        ).subscribe(
            {
                next: () => {
                    this.types.push({ Code: "Plug", Definition: "Plug & Abandon" } as Domain);
                    this.types.push({ Code: "Drill", Definition: "Drill a New Well" } as Domain);
                    this.types.push({ Code: "Reissue a Previously Issued Permit", Definition: "Reissue a Previously Issued Permit" } as Domain);
                    this.types.push({ Code: "Permit to Revise Location", Definition: "Permit to Revise Location" } as Domain);
                    this.types.push({ Code: "Permit to Plug Back", Definition: "Permit to Plug Back" } as Domain);
                    this.types.push({ Code: "Permit to Convert", Definition: "Permit to Convert" } as Domain);
                    this.types.push({ Code: "Permit to Reopen", Definition: "Permit to Reopen" } as Domain);
                    this.types.push({ Code: "Permit to Deepen", Definition: "Permit to Deepen" } as Domain);
                },
                error: (error) => {
                    console.error(error);
                    this.dialogService.showError();
                }
            }
        );
        this.subscription.add(type$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    apiSelected(e: Event): void {
        let selected: string[] = [];
        if (this.gridApi != null) {
            let rows = this.gridApi.getSelectedRows();
            for (let r = 0; r < rows.length; r++) {
                selected.push((rows[r] as Option).id);
            }
            if (selected.length > 0) {
                this.form.get("api")?.setValue(selected[0]);
            } else {
                this.form.get("api")?.setValue("");
            }        
        }
    }

	bytesToArray(base64: string): Uint8Array {
		const binaryString = window.atob(base64);
		const len = binaryString.length;
		const bytes = new Uint8Array(len);
		for (let i = 0; i < len; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		return bytes;
	}


    documentsSelected(e: Event): void {
        let selected: RegeneratedPermitResult[] = [];
        if (this.regeneratedGridApi != null) {
            let rows = this.regeneratedGridApi.getSelectedRows();
            for (let r = 0; r < rows.length; r++) {
                selected.push((rows[r] as RegeneratedPermitResult));
            }
        }        
        this.form.get("documents")?.setValue(selected);
    }

	download(data: RegeneratedPermitResult) {
		const fileName = `${data.id}.pdf`;
		const bytes = this.bytesToArray(data.data);
		const safeBytes = new Uint8Array(Uint8Array.from(bytes));
		// Pass Uint8Array directly to Blob
		const blob = new Blob([safeBytes], { type: "application/pdf" });
		const blobUri = window.URL.createObjectURL(blob);
		const anchor = this.downloadLink.nativeElement as HTMLAnchorElement;
		anchor.href = blobUri;
		anchor.download = fileName;
		anchor.click();
	}


    formatType(field: string): any {
        return (obj: any) => {
            let definition: string = "Unknown";
			if (obj.data != null && obj.data[field] != null) {
                let code = obj.data[field];
                let domain = this.types.find(d => d.Code == code);
                if (domain != null) definition = domain.Definition;
				if (obj.data.proposal != null && obj.data.proposal.wellType != null && obj.data.proposal.wellType.type == 'ST_R') {
					if (this.applicationData != null) {
						let app = this.applicationData.find(a => a.applicationNumber === obj.data["id"]);
						if (app != null) {
							definition = `${definition} - Stratigraphic`;
						}
					}					
				}
            }
            return definition;
        }
    }

    noDocumentsSelected(): void {
        let dialog: Dialog = new Dialog("You must select at least one regenerated permit to upload.", [new DialogButton("OK", false, () => { })]);
        dialog.toggleDialog(true);
        this.dialogService.updateDialog(dialog);
    }

    noPermitsSelected(): void {
        let dialog: Dialog = new Dialog("You must select at least one permit to regenerate.", [new DialogButton("OK", false, () => { })]);
        dialog.toggleDialog(true);
        this.dialogService.updateDialog(dialog);
    }

    noWellsSelected(): void {
        let dialog: Dialog = new Dialog("You must select an API Number to search for permits.", [new DialogButton("OK", false, () => { })]);
        dialog.toggleDialog(true);
        this.dialogService.updateDialog(dialog);
    }

    onSchematicUpdated(evt: Permit): void {
        let existingIndex = this.selectedPermits.findIndex(p => p.id === evt.id);
        if (existingIndex !== -1) {
            this.selectedPermits.splice(existingIndex, 1);
        }
        this.selectedPermits.push(evt);
    }

    permitSelected(e: Event): void {
        let selected: Permit[] = [];
        if (this.permitGridApi != null) {
             let rows = this.permitGridApi.getSelectedRows();
            for (let r = 0; r < rows.length; r++) {
                let pmt = rows[r] as Permit;
                if (pmt.proposal == null ||  pmt.proposal.wellType == null || pmt.proposal.wellType.type != "ST_R") {
                    if (pmt.casingPlan != null && this.selectedPermits.findIndex(p => p.id === pmt.id) === -1) {
                        this.permitToPreviewSchematic = pmt;
                        this.previewComponent.toggle(true);
                    }   
                }           
                selected.push(pmt);
            }
        }       
        this.form.get("permits")?.setValue(selected);
    }

     regeneratePermits(): void {
        let permitCtrl = this.form.get("permits");
        if (permitCtrl != null && permitCtrl.valid) {
            let permitList: Permit[] = <Permit[]>permitCtrl.value;
            let list: Observable<RegeneratedPermitResult | null>[] = [];
            for (let p = 0; p < permitList.length; p++) {
                let request: RegeneratePermitRequest = {
                    id: permitList[p].id,
                    schematic: (permitList[p].casingPlan != null) ? permitList[p].casingPlan.schematic : ""
                } as RegeneratePermitRequest;
                 let obs$ = this.permitService.regeneratePermit(request).pipe(
                        catchError((error) => {                            
                            console.error(error);
                            this.dialogService.showError();
                            return of(null);
                        }),
                        tap((res: RegeneratedPermitResult | null) => {  })
                    );
                list.push(obs$);
            }
            this.generator$ = combineLatest(list).pipe(
                map((data: any) => {
                    return data.filter(d => d != null);
                }
                ));
            this.setStep(PermitRegenerationSteps.ConfirmDocuments);
        } else {
            this.noPermitsSelected();
        }
    }

    restart(): void {
        this.setStep(PermitRegenerationSteps.SelectWell);
        if (this.gridApi != null) {
            this.gridApi.deselectAll();
        }
        this.form.reset();
    }

    searchPermits(): void {
        let apiCtrl = this.form.get("api");
		if (apiCtrl != null && apiCtrl.valid) {
			const spec = {
				applicationTypes: [ApplicationType.Stratigraphic],
				applicationStates: [ApplicationState.Approved]
			} as MetadataSearchSpec;
			let app$ = this.applicationService.searchMetadata(spec).pipe(
				tap((data: PendingFlow[]) => {
					this.applicationData = data;
			}));
			this.permit$ = app$.pipe(switchMap(() => {
				return this.permitService.getWellPermits(apiCtrl.value).pipe(
					catchError((error) => {
						console.error(error);
						this.dialogService.showError();
						return of([]);
					}),
					map((permits: Permit[]) => {
						if (permits == null) return [];
						return permits.filter(p => p != null && (p.id.startsWith("PR") || p.id.startsWith("LP")));
					})
				)
			}));
            this.setStep(PermitRegenerationSteps.SelectPermits);
        } else {
            this.noWellsSelected();
        }       
    }

    setStep(setTo: PermitRegenerationSteps): void {
        this.currentStep = setTo;
        switch (setTo) {
            case PermitRegenerationSteps.SelectWell:
                this.subheaderService.updateSubHeader(this.step1Header);
                this.form.get("permits")?.reset();
                this.form.get("permits")?.setValue([]);
                this.form.get("documents")?.reset();
                break;
            case PermitRegenerationSteps.SelectPermits:
                this.subheaderService.updateSubHeader(this.step2Header);
                this.form.get("documents")?.reset();
                break;
            case PermitRegenerationSteps.ConfirmDocuments:
                this.subheaderService.updateSubHeader(this.step3Header);
                break;
            case PermitRegenerationSteps.SaveDocuments:
                this.subheaderService.updateSubHeader(this.step4Header);
                break;
        }
    }

    uploadDocuments(): void {
        let selectedUploads: RegeneratedPermitResult[] = this.form.get("documents")?.value;
        if (selectedUploads != null && selectedUploads.length > 0) {
            this.setStep(PermitRegenerationSteps.SaveDocuments);
            let obs$: Observable<RegeneratedPermitResult | null>[] = [];
            for (let u = 0; u < selectedUploads.length; u++) {
                (selectedUploads[u] as RegeneratedPermitResult).uploadStatus = DocumentUploadStatus.Uploading;
                this.uploadingDocuments.push(selectedUploads[u]);
                let obs = this.permitService.uploadRegeneratedPermit(selectedUploads[u]).pipe(
                    catchError((error) => {
                        console.error(error);
                        let id = error.error.toString().split(':');
                        if (id.length > 0) {
                            let documentIndex = this.uploadingDocuments.findIndex(d => d.id === id[0]);
                            if (documentIndex != -1) {
                                let document = this.uploadingDocuments[documentIndex];
                                this.uploadingDocuments.splice(documentIndex, 1);
                                document.uploadStatus = DocumentUploadStatus.Error;
                                this.uploadingDocuments.push(document);
                            }
                        }                        
                        return of(null);
                    }),
                    tap((result: RegeneratedPermitResult | null) => {
                        if (result != null) {
                            result.uploadStatus = DocumentUploadStatus.Complete;
                            let documentIndex = this.uploadingDocuments.findIndex(d => d.id == result.id);
                            if (documentIndex != -1) {
                                this.uploadingDocuments.splice(documentIndex, 1);
                                this.uploadingDocuments.push(result);
                            }
                        }
                    })
                );
                obs$.push(obs);
            }
            let subs$ = concat(...obs$).subscribe({
                next: (data) => {
                    this.showResults.next(false);
                    setTimeout(() => { this.showResults.next(true); }, 200);
                },
                error: (error) => {
                    console.error(error);
                    this.dialogService.showError();
                }
            });
            this.subscription.add(subs$);
        } else {
            this.noDocumentsSelected();
        }
    }
}

export enum PermitRegenerationSteps {
    SelectWell = 1,
    SelectPermits = 2,
    ConfirmDocuments = 3,
    SaveDocuments = 4
}


